/**
 * Rupees on screen, integer paise everywhere else (CLAUDE.md rule 2). These only
 * convert what a person types or reads; they never round a computed amount (that is
 * `packages/pricing`'s job). A value with more than two decimals is refused, not
 * rounded.
 */

const GROUPING = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** 125050 → "1,250.50" (Indian digit grouping); always two decimals. */
export function formatRupees(paise: number | bigint): string {
  const p = typeof paise === 'bigint' ? paise : BigInt(Math.trunc(paise));
  const negative = p < 0n;
  const abs = negative ? -p : p;
  const rupees = abs / 100n;
  const rest = abs % 100n;
  const whole = GROUPING.format(rupees);
  return `${negative ? '-' : ''}${whole}.${rest.toString().padStart(2, '0')}`;
}

/** 125050 → "₹1,250.50". */
export function formatMoney(paise: number | bigint): string {
  const text = formatRupees(paise);
  return text.startsWith('-') ? `-₹${text.slice(1)}` : `₹${text}`;
}

/** 125050 → "1250.50" (no grouping): the editable text of an amount. */
export function paiseToInput(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '';
  const negative = paise < 0;
  const abs = Math.abs(Math.trunc(paise));
  const rupees = Math.floor(abs / 100);
  const rest = abs % 100;
  return `${negative ? '-' : ''}${rupees}.${String(rest).padStart(2, '0')}`;
}

export type ParsedAmount =
  | { readonly ok: true; readonly paise: number | null }
  | { readonly ok: false; readonly message: string };

const AMOUNT = /^(\d{1,9})(?:\.(\d{0,2}))?$/;

/**
 * What a person typed in a rupee field → paise. Empty → null. Accepts "1,250.5",
 * "₹ 99", "12."; refuses negatives, letters and a third decimal.
 */
export function parseRupees(text: string): ParsedAmount {
  const cleaned = text.replace(/[₹,\s]/g, '').replace(/^Rs\.?/i, '');
  if (cleaned === '') return { ok: true, paise: null };
  const m = AMOUNT.exec(cleaned);
  if (!m) {
    return /^\d*\.\d{3,}$/.test(cleaned)
      ? { ok: false, message: 'Use at most two decimals (paise)' }
      : { ok: false, message: 'Enter an amount in rupees, like 120 or 99.50' };
  }
  const rupees = Number(m[1]);
  const fraction = (m[2] ?? '').padEnd(2, '0');
  return { ok: true, paise: rupees * 100 + Number(fraction) };
}

/** 250 → "2.5" (basis points as a percentage, no trailing zeros). */
export function bpToPercentText(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return '';
  const whole = Math.trunc(bp / 100);
  const rest = Math.abs(bp % 100);
  if (rest === 0) return String(whole);
  const frac = String(rest).padStart(2, '0').replace(/0$/, '');
  return `${bp < 0 && whole === 0 ? '-' : ''}${whole}.${frac}`;
}

/** 250 → "2.5%". */
export function formatPercent(bp: number): string {
  return `${bpToPercentText(bp)}%`;
}

export type ParsedPercent =
  | { readonly ok: true; readonly bp: number | null }
  | { readonly ok: false; readonly message: string };

const PERCENT = /^(\d{1,3})(?:\.(\d{0,2}))?$/;

/** "2.5" or "2.5%" → 250 basis points. Empty → null. At most two decimals, at most `max` bp. */
export function parsePercent(text: string, max = 10_000): ParsedPercent {
  const cleaned = text.replace(/[%\s]/g, '');
  if (cleaned === '') return { ok: true, bp: null };
  const m = PERCENT.exec(cleaned);
  if (!m) return { ok: false, message: 'Enter a percentage like 5 or 2.5' };
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (bp > max) return { ok: false, message: `At most ${bpToPercentText(max)}%` };
  return { ok: true, bp };
}
